import {
  OPENAI_AD_CONVERSION_EVENTS,
  type OpenAiAdConversionDataType,
  type OpenAiAdConversionEvent,
} from './openai-ad-conversion-events';
import { createScriptLoadQueue } from './script-load-queue';

type OpenAiEventName = 'page_viewed' | OpenAiAdConversionEvent;

type OpenAiEventData = { type: 'contents' | OpenAiAdConversionDataType };

type OpenAiEventOptions = { event_id: string };

declare global {
  function oaiq(
    command: 'measure',
    eventName: OpenAiEventName,
    eventData: OpenAiEventData,
    options?: OpenAiEventOptions,
  ): void;
}

type QueuedEvent = { eventName: OpenAiEventName; options?: OpenAiEventOptions };

/**
 * `page_viewed` uses OpenAI's `contents` data shape; each Ad Conversion uses
 * the shape its entry in `OPENAI_AD_CONVERSION_EVENTS` names.
 */
const eventDataFor = (eventName: OpenAiEventName): OpenAiEventData => ({
  type: OPENAI_AD_CONVERSION_EVENTS.find(({ value }) => value === eventName)?.dataType ?? 'contents',
});

function measure({ eventName, options }: QueuedEvent) {
  if (options) {
    oaiq('measure', eventName, eventDataFor(eventName), options);
  } else {
    oaiq('measure', eventName, eventDataFor(eventName));
  }
}

const { queue: eventQueue, flush: flushOpenAiQueue, enqueue } = createScriptLoadQueue<QueuedEvent>({
  flushHook: '__flushOaiqQueue',
  send: measure,
});

/**
 * Sends an event to the OpenAI Pixel. The pixel loads `lazyOnload`, and only
 * after Marketing Consent, so an event that fires after consent but before its
 * init script has run (a fast form submit, the first page view) is queued and
 * flushed by that script, through the same queue `trackGtagEvent` uses for
 * Google. An event fired before consent is dropped, not queued, and only then
 * does this return `false`.
 */
export function trackOpenAiEvent(eventName: OpenAiEventName, options?: OpenAiEventOptions): boolean {
  if (typeof oaiq === 'function') {
    measure({ eventName, options });
    return true;
  }
  return enqueue({ eventName, options });
}

export { eventQueue, flushOpenAiQueue };
